import { ControlledTextInput } from '@components/ControlledTextInput';
import { SelectInput } from '@gurupay/gp-ui-library';
import { Controller, type FieldValues, type Path, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type BeneficiaryTypeInputProps<T extends FieldValues> = {
	prefix: Path<T>;
	translatePrefix: string;
	disabled?: boolean;
};

export const BeneficiaryTypeInput = <T extends FieldValues>({
	translatePrefix,
	prefix,
	disabled,
}: BeneficiaryTypeInputProps<T>) => {
	const { t: tCommon } = useTranslation();
	const { t } = useTranslation('', { keyPrefix: translatePrefix });
	const BENEFICIARY_ID_TYPE = [
		{ value: 'PERSONAL_ID_NUMBER', label: tCommon('templatesForm.idCard') },
		{ value: 'ID_NUMBER', label: tCommon('templatesForm.passport') },
		{ value: 'DRIVER_LICENSE_NUMBER', label: tCommon('templatesForm.driverLicense') },
		{ value: 'PHONE_NUMBER', label: tCommon('templatesForm.phone') },
	];
	const { control, watch, setValue, clearErrors } = useFormContext<T>();
	const isTypeSelected = !!watch(`${prefix}.type` as Path<T>);

	const clearType = () => {
		setValue(
			prefix as Path<T>,
			{
				type: undefined,
				value: undefined,
			} as T[Path<T>],
			{
				shouldValidate: false,
				shouldDirty: true,
				shouldTouch: true,
			},
		);
	};
	const handleTypeChange = (type?: string) => {
		if (!type) {
			clearErrors(prefix);
			clearType();
		} else {
			setValue(`${prefix}.type` as Path<T>, type as T[Path<T>]);
		}
	};

	return (
		<div className="space-y-space-16" data-test-id={prefix}>
			<Controller
				control={control}
				name={`${prefix}.type` as Path<T>}
				render={({ field: { onChange: _, ...field } }) => (
					<SelectInput
						{...field}
						className="max-w-[500px]"
						isDisabled={disabled}
						label={t('idType')}
						onChange={handleTypeChange}
						options={BENEFICIARY_ID_TYPE}
						showClear
					/>
				)}
			/>
			{isTypeSelected && (
				<ControlledTextInput
					disabled={disabled}
					label={t('idValue')}
					name={`${prefix}.value`}
					rules={{
						required: isTypeSelected && tCommon('payments.newPayment.fieldIsRequired'),
					}}
				/>
			)}
		</div>
	);
};
